import { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, AlertTriangle, PoundSterling, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

export default function CalculatorPage() {
  const [income1, setIncome1] = useState("");
  const [income2, setIncome2] = useState("");
  const [deposit, setDeposit] = useState("");
  const [term, setTerm] = useState("25");
  const [interestRate, setInterestRate] = useState("5.0");
  const [monthlyDebts, setMonthlyDebts] = useState("");
  const [result, setResult] = useState(null);

  const calculateAffordability = (e) => {
    e.preventDefault();

    const totalIncome = (parseFloat(income1) || 0) + (parseFloat(income2) || 0);
    const depositAmount = parseFloat(deposit) || 0;
    const debts = parseFloat(monthlyDebts) || 0;
    const termYears = parseInt(term);
    const rate = parseFloat(interestRate) / 100;

    // Standard UK mortgage affordability: typically 4-4.5x income
    const multiplier = debts > 0 ? 3.5 : 4.5;
    const maxBorrowing = totalIncome * multiplier;
    const totalPurchasePrice = maxBorrowing + depositAmount;

    // Monthly mortgage payment calculation (principal + interest)
    const monthlyRate = rate / 12;
    const numPayments = termYears * 12;
    const monthlyPayment =
      monthlyRate > 0
        ? (maxBorrowing * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) /
          (Math.pow(1 + monthlyRate, numPayments) - 1)
        : maxBorrowing / numPayments;

    setResult({
      maxBorrowing: Math.round(maxBorrowing),
      totalPurchasePrice: Math.round(totalPurchasePrice),
      monthlyPayment: Math.round(monthlyPayment),
      multiplier,
      depositAmount: Math.round(depositAmount),
      ltv: Math.round((maxBorrowing / totalPurchasePrice) * 100),
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const resetCalculator = () => {
    setIncome1("");
    setIncome2("");
    setDeposit("");
    setTerm("25");
    setInterestRate("5.0");
    setMonthlyDebts("");
    setResult(null);
  };

  return (
    <div data-testid="calculator-page">
      {/* Hero Banner */}
      <section className="relative bg-[#111111] py-32 lg:py-40">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#d64241] font-semibold text-sm tracking-widest uppercase mb-3 font-body">
            Affordability Calculator
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            How Much Can<br />
            <span className="text-[#d64241]">I Borrow?</span>
          </h1>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section data-testid="calculator-disclaimer-banner" className="bg-[#fef2f2] border-b border-[#f4adad]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#d64241] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#732322] font-body leading-relaxed">
              <strong>Important Disclaimer:</strong> The figures provided by this calculator are
              estimates only and are intended as a general guide. Actual borrowing amounts, interest
              rates, and monthly payments can differ significantly from bank to bank and lender to
              lender. Your actual mortgage offer will depend on your individual circumstances,
              credit history, and the lender's specific criteria. For accurate figures, please{" "}
              <Link to="/contact" className="underline font-semibold hover:text-[#5c1c1b]">
                contact us
              </Link>{" "}
              for a personalised consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section data-testid="calculator-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Calculator Form */}
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-black tracking-tight mb-4">
                Calculate Your Borrowing Power
              </h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 font-body">
                Enter your financial details below to get an estimate of how much you could
                potentially borrow for your mortgage.
              </p>

              <form
                onSubmit={calculateAffordability}
                data-testid="calculator-form"
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                    Your Annual Income (before tax)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-body text-sm">£</span>
                    <Input
                      data-testid="calculator-income1"
                      type="number"
                      placeholder="e.g. 35000"
                      value={income1}
                      onChange={(e) => setIncome1(e.target.value)}
                      className="h-12 pl-7 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      required
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                    Second Applicant's Annual Income (optional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-body text-sm">£</span>
                    <Input
                      data-testid="calculator-income2"
                      type="number"
                      placeholder="e.g. 30000"
                      value={income2}
                      onChange={(e) => setIncome2(e.target.value)}
                      className="h-12 pl-7 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                    Deposit Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-body text-sm">£</span>
                    <Input
                      data-testid="calculator-deposit"
                      type="number"
                      placeholder="e.g. 25000"
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      className="h-12 pl-7 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      required
                      min="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                      Mortgage Term (years)
                    </label>
                    <Select value={term} onValueChange={setTerm}>
                      <SelectTrigger
                        data-testid="calculator-term-select"
                        className="h-12 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 15, 20, 25, 30, 35].map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y} years
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                      Interest Rate (%)
                    </label>
                    <Input
                      data-testid="calculator-rate"
                      type="number"
                      step="0.1"
                      placeholder="e.g. 5.0"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="h-12 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      required
                      min="0"
                      max="20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5 font-body">
                    Monthly Debt Payments (optional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-body text-sm">£</span>
                    <Input
                      data-testid="calculator-debts"
                      type="number"
                      placeholder="e.g. 200"
                      value={monthlyDebts}
                      onChange={(e) => setMonthlyDebts(e.target.value)}
                      className="h-12 pl-7 rounded-lg border-slate-200 focus:border-[#d64241] focus:ring-[#d64241]"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 font-body">
                    Include credit cards, loans, car finance, etc.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    data-testid="calculator-submit-btn"
                    className="flex-1 bg-[#d64241] hover:bg-[#b53736] text-white rounded-full py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                  <Button
                    type="button"
                    onClick={resetCalculator}
                    data-testid="calculator-reset-btn"
                    variant="outline"
                    className="rounded-full py-6 px-6 text-base font-medium border-slate-200 hover:bg-slate-50"
                  >
                    Reset
                  </Button>
                </div>
              </form>
            </div>

            {/* Results */}
            <div>
              {result ? (
                <div
                  data-testid="calculator-results"
                  className="bg-[#111111] rounded-2xl p-8 lg:p-10 text-white sticky top-28"
                >
                  <h3 className="text-2xl font-semibold mb-2">Your Results</h3>
                  <p className="text-slate-300 text-sm font-body mb-8">
                    Based on the information you provided
                  </p>

                  <div className="space-y-6">
                    <div className="bg-white/10 rounded-xl p-6">
                      <p className="text-sm text-slate-300 font-body mb-1">
                        Maximum You Could Borrow
                      </p>
                      <p
                        data-testid="result-max-borrowing"
                        className="text-3xl lg:text-4xl font-bold text-[#d64241] font-heading"
                      >
                        {formatCurrency(result.maxBorrowing)}
                      </p>
                      <p className="text-xs text-slate-400 font-body mt-1">
                        Based on {result.multiplier}x income multiplier
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-xl p-5">
                        <p className="text-xs text-slate-300 font-body mb-1">
                          Est. Property Value
                        </p>
                        <p
                          data-testid="result-property-value"
                          className="text-xl font-bold text-white"
                        >
                          {formatCurrency(result.totalPurchasePrice)}
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-5">
                        <p className="text-xs text-slate-300 font-body mb-1">
                          Est. Monthly Payment
                        </p>
                        <p
                          data-testid="result-monthly-payment"
                          className="text-xl font-bold text-white"
                        >
                          {formatCurrency(result.monthlyPayment)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-xl p-5">
                        <p className="text-xs text-slate-300 font-body mb-1">Your Deposit</p>
                        <p className="text-xl font-bold text-white">
                          {formatCurrency(result.depositAmount)}
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-5">
                        <p className="text-xs text-slate-300 font-body mb-1">
                          Loan-to-Value (LTV)
                        </p>
                        <p className="text-xl font-bold text-white">{result.ltv}%</p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-6 bg-white/20" />

                  {/* Disclaimer in results */}
                  <div className="flex items-start gap-2 mb-6">
                    <Info className="w-4 h-4 text-[#d64241] flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-400 font-body leading-relaxed">
                      These are estimates only. Actual amounts may vary significantly between
                      lenders based on your credit score, employment status, and other factors.
                    </p>
                  </div>

                  <Link to="/contact">
                    <Button
                      data-testid="result-cta-btn"
                      className="w-full bg-[#d64241] hover:bg-[#b53736] text-white rounded-full py-6 text-base font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
                    >
                      Get Expert Advice
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div
                  data-testid="calculator-placeholder"
                  className="bg-slate-50 rounded-2xl p-8 lg:p-10 border border-slate-100 sticky top-28 text-center"
                >
                  <div className="w-20 h-20 rounded-2xl bg-[#fef2f2] flex items-center justify-center mx-auto mb-6">
                    <Calculator className="w-10 h-10 text-[#d64241]" />
                  </div>
                  <h3 className="text-2xl font-semibold text-black mb-3">
                    Ready to Calculate?
                  </h3>
                  <p className="text-slate-600 font-body mb-6 leading-relaxed">
                    Fill in the form on the left to see an estimate of how much you could borrow
                    for your mortgage. It only takes a minute!
                  </p>
                  <div className="space-y-3 text-left max-w-xs mx-auto">
                    {[
                      "Enter your annual income",
                      "Add your deposit amount",
                      "Choose your preferred term",
                      "Get instant results",
                    ].map((step, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#d64241] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-sm text-slate-700 font-body">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Full Disclaimer */}
      <section data-testid="full-disclaimer-section" className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 lg:p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#d64241]" />
              <h3 className="text-xl font-semibold text-black">Important Information</h3>
            </div>
            <div className="space-y-3 text-sm text-slate-600 font-body leading-relaxed">
              <p>
                This mortgage affordability calculator is provided for illustrative purposes only
                and should not be considered as financial advice. The results are based on
                simplified calculations and general assumptions.
              </p>
              <p>
                <strong className="text-slate-800">
                  Actual borrowing amounts can differ significantly from bank to bank and lender to
                  lender.
                </strong>{" "}
                Each lender has their own criteria for assessing mortgage applications, including
                but not limited to:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your credit history and credit score</li>
                <li>Employment type and stability</li>
                <li>Existing financial commitments and debts</li>
                <li>The type and location of the property</li>
                <li>Current market conditions and lending policies</li>
                <li>Stress testing at higher interest rates</li>
              </ul>
              <p>
                For accurate and personalised mortgage advice tailored to your specific
                circumstances, we strongly recommend speaking with one of our qualified mortgage
                advisors.
              </p>
              <div className="pt-3">
                <Link to="/contact">
                  <Button
                    data-testid="disclaimer-cta-btn"
                    className="bg-[#d64241] hover:bg-[#b53736] text-white rounded-full px-6 py-5 text-sm font-semibold shadow-lg shadow-[#d64241]/25 hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Speak to an Advisor
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
